import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { color, typography } from 'styled-system';
import { Field, Form } from 'formik';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Select from 'react-select';

import Text from '../../../../../shared/_TBComponents/Text';
import useReseller from '../../hooks/useReseller';
import useResellerPrograms from '../../hooks/useResellerPrograms';

import 'react-datepicker/dist/react-datepicker.css';
import { _ } from 'core-js';

const AnotherWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CheckboxWrapper = styled('div')`
  padding-top: 12px;
`;

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin-top: 5px;
`;

const FieldErrorForOffsetDate = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin-bottom: 4px;
  margin-left: 2px;
`;

const FirstWorkoutContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FormGroup = styled('div')`
  width: 100%;

  label {
    width: 100%;
  }

  button {
    margin-bottom: 20px;
  }

  .easy-join {
    margin-bottom: 15px;
  }

  .caveat-text {
    font-size: 11px;
    margin-top: 5px;
  }

  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }

  .form-control {
    min-height: 30px;
    width: 100%;
  }

  .alt-form-control {
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    border-bottom-left-radius: 0px;
    border-top-left-radius: 0px;
    color: #495057;
    display: block;
    font-size: 1rem;
    font-weight: 400;
    height: calc(1.5em + 0.75rem + 2px);
    line-height: 1.5;
    min-height: 30px;
    padding: 0.375rem 0.75rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    width: 100%;
  }

  .btow {
    background: black;
    border: 1px solid black;
    border-radius: 0.25rem;
    border-bottom-right-radius: 0px;
    border-top-right-radius: 0px;
    color: white;
    font-size: 12px;
    font-weight: 800;
    height: 100%;
    min-width: 210px;
    padding: 9px;
    padding-left: 15px;
    width: 100%;
  }

  .checkbox-label {
    display: flex;
    margin-top: 10px;
  }

  .checkbox {
    margin-right: 10px;
    margin-top: 3px;
  }
`;

const InnerFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: left;
  margin-top: 5px;
  width: 80%;

  .formik-form {
    width: 100%;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;

  .modal-button {
    align-items: center;
    display: flex;
  }
`;

const OuterFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .divider-line {
    border-top: 2px solid rgb(232, 232, 232);
    display: flex;
    margin-bottom: 10px;
    margin-top: 5px;
    width: calc(100% + 80px);
  }
`;

const SubText = styled('div')`
  color: ${(props) => props.theme.colors.text || '#444444'};
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  ${typography}
  ${color}
`;

const TextBoxContainer = styled('div')`
  border-color: ${(props) => props.borderColor};
  border-radius: 4px;
  border-style: solid;
  border-width: ${(props) => props.borderWidth};
  cursor: pointer;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  height: 165px;
  margin-left: ${(props) => props.marginLeft};
  margin-right: ${(props) => props.marginRight};
`;

const TextBoxContent = styled('div')`
  font-size: 16px;
  font-weight: 300;
  margin-left: 6px;
  margin-right: 6px;
`;

const TextBoxContentContainer = styled('div')`
  align-items: center;
  display: flex;
  text-align: center;
`;

const TextBoxOuterWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const TextBoxTitle = styled('div')`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
  margin-top: 15px;
  text-transform: uppercase;
`;

const TextBoxTitleContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  text-align: center;
`;

const TitlePlusErrorWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const UpdateProgramSlide2 = ({
  clickedCalendar,
  clickedOffsetDate,
  flattenedCalendars,
  formProps,
  page2Errors,
  program,
  programSourceOptions,
  programType,
  savedWorkoutError,
  selectedCalendar,
  selectedDate,
  setClickedCalendar,
  setClickedOffsetDate,
  setProgramType,
  setSavedWorkoutError,
  setSelectedCalendar,
  setSelectedDate,
}) => {
  const theme = useTheme();

  const {
    calendars, currentUser, savedWorkouts, userGroups,
  } = useReseller();

  const {
    firstWorkout,
    firstWorkoutError,
    handleFetchFirstWorkout,
  } = useResellerPrograms();

  const [firstTouchDown, setFirstTouchDown] = useState(true);
  const lastFetchedCalendarIdRef = useRef(null);

  const isCalendarSelected = useMemo(
    () => Object.keys(selectedCalendar || {}).length > 0,
    [selectedCalendar],
  );

  const isOriginalProgramPreBuilt = useMemo(
    () => !formProps.touched.programType
      && program.calendarOffsetDate
      && !formProps.touched.calendarId,
    [
      formProps.touched.programType,
      formProps.touched.calendarId,
      program.calendarOffsetDate,
    ],
  );

  const isOriginalProgramOnGoing = useMemo(
    () => !formProps.touched.programType
      && !program.calendarOffsetDate
      && !formProps.touched.calendarId,
    [
      formProps.touched.programType,
      formProps.touched.calendarId,
      program.calendarOffsetDate,
    ],
  );

  useEffect(() => {
    if (firstWorkout && !firstWorkoutError && !clickedOffsetDate) {
      const date = moment(firstWorkout.date);
      setSelectedDate(date);
      formProps.setFieldValue('calendarOffsetDate', date.format('YYYY-MM-DD'));
    }
  }, [
    firstWorkout,
    firstWorkoutError,
    clickedOffsetDate,
    setSelectedDate,
  ]);

  useEffect(() => {
    const accountCode = currentUser?.accountCode;
    const calendarId = selectedCalendar?.id;

    if (!isCalendarSelected || !calendarId) {
      lastFetchedCalendarIdRef.current = null;
      return;
    }

    if (
      !firstTouchDown
      && accountCode
      && lastFetchedCalendarIdRef.current !== calendarId
    ) {
      lastFetchedCalendarIdRef.current = calendarId;
      handleFetchFirstWorkout({
        accountCode,
        calendarId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser?.accountCode,
    selectedCalendar?.id,
    isCalendarSelected,
    firstTouchDown,
  ]);

  useEffect(() => {
    if (
      program.calendarId
      && calendars?.length > 0
      && !clickedCalendar
      && !clickedOffsetDate
      && program.calendarOffsetDate
    ) {
      const date = moment(program.calendarOffsetDate);
      setSelectedDate(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedCalendars, clickedCalendar, clickedOffsetDate]);

  const handleProgramSourceChange = () => {};

  const handleCalendarChange = (options) => {
    setClickedCalendar(true);
    setFirstTouchDown(false);

    if (options) {
      formProps.setFieldTouched('calendarId', true);
      formProps.setFieldValue('calendarId', options.id);
      setSelectedCalendar(options);
    } else {
      lastFetchedCalendarIdRef.current = null;
      formProps.setFieldValue('calendarId', 0);
      formProps.setFieldValue('calendarOffsetDate', '');
      formProps.setFieldValue('programType', null);
      formProps.setFieldValue('mondayStart', null);
      formProps.setFieldTouched('calendarId', true);
      setProgramType(null);
      setSelectedDate(null);
      setSelectedCalendar('');
    }
  };

  const handleProgramTypeClick = (selectedProgramType) => {
    const accountCode = currentUser?.accountCode;
    const calendarId = selectedCalendar?.id;
    formProps.setFieldValue('programType', selectedProgramType);
    formProps.setFieldTouched('programType', true);
    setProgramType(selectedProgramType);

    if (selectedProgramType === 'ONGOING') {
      formProps.setFieldValue('calendarOffsetDate', '');
      formProps.setFieldValue('mondayStart', null);
    } else if (!selectedDate) {
      if (accountCode && calendarId && lastFetchedCalendarIdRef.current !== calendarId) {
        lastFetchedCalendarIdRef.current = calendarId;
        handleFetchFirstWorkout({
          accountCode,
          calendarId,
        });
      }
    } else {
      formProps.setFieldValue(
        'calendarOffsetDate',
        moment(selectedDate).format('YYYY-MM-DD'),
      );
    }
  };

  const handleDateChange = (date) => {
    setClickedOffsetDate(true);
    if (date) {
      const newDate = moment(date);
      const finalDateString = newDate.format('YYYY-MM-DD');
      formProps.setFieldValue('calendarOffsetDate', finalDateString);
    } else {
      formProps.setFieldValue('calendarOffsetDate', '');
    }
    setSelectedDate(date);
  };

  const handleSavedWorkoutsChange = (options) => {
    formProps.setFieldValue('savedWorkouts', options || []);

    if (options?.length > 0 && savedWorkoutError) {
      setSavedWorkoutError('');
    }
  };

  const handleGroupsChange = (options) => {
    formProps.setFieldValue('groups', options || []);
  };

  const handleMondayStartChange = () => {
    formProps.setFieldTouched('mondayStart', true);
    formProps.setFieldValue('mondayStart', !formProps.values.mondayStart);
  };

  const handleAutoStartChange = () => {
    formProps.setFieldValue(
      'autoStartProgram',
      !formProps.values.autoStartProgram,
    );
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
  };

  const getBorderColor = (isSelected) => (isSelected ? theme.colors.green : theme.colors.border || 'hsl(0,0%,80%)');

  const getBorderWidth = (isSelected) => (isSelected ? '2px' : '1px');

  const formGroupTextProps = { marginBottom: '5px', marginTop: '10px' };

  const isPreBuiltSelected = programType === 'PRE-BUILT' || isOriginalProgramPreBuilt;
  const isOngoingSelected = programType === 'ONGOING' || isOriginalProgramOnGoing;

  return (
    <ModalWrapper>
      <AnotherWrapper>
        <OuterFolderFormWrapper>
          <InnerFolderFormWrapper>
            <Form className='formik-form'>
              <FormGroup>
                <TitlePlusErrorWrapper>
                  <Text
                    styledTextProps={{ fontWeight: 700 }}
                    styledWrapperProps={formGroupTextProps}
                  >
                    Program Source
                  </Text>
                </TitlePlusErrorWrapper>
                <Select
                  className='first-stage-select'
                  defaultValue={
                    formProps.values?.programSource || {
                      label: 'Calendar',
                      value: 'calendar',
                    }
                  }
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  isDisabled
                  options={programSourceOptions}
                  styles={customStyles}
                  onChange={handleProgramSourceChange}
                />
              </FormGroup>
              {formProps.values.programSource.value === 'calendar' ? (
                <>
                  <FormGroup>
                    <TitlePlusErrorWrapper>
                      <Text
                        styledTextProps={{ fontWeight: 700 }}
                        styledWrapperProps={formGroupTextProps}
                      >
                        Calendar Assignment
                      </Text>
                    </TitlePlusErrorWrapper>
                    <Select
                      className='first-stage-select'
                      defaultValue={selectedCalendar}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isClearable={isCalendarSelected}
                      isDisabled={formProps.values.savedWorkouts?.length > 0}
                      isSearchable
                      options={flattenedCalendars}
                      styles={customStyles}
                      onChange={handleCalendarChange}
                    />
                  </FormGroup>
                  {isCalendarSelected && (
                    <FormGroup>
                      <TitlePlusErrorWrapper>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Program Type
                        </Text>
                        {formProps.errors.programType && page2Errors && (
                          <FieldError className='error-text'>
                            {formProps.errors.programType}
                          </FieldError>
                        )}
                      </TitlePlusErrorWrapper>
                      <TextBoxOuterWrapper>
                        <TextBoxContainer
                          borderColor={getBorderColor(isPreBuiltSelected)}
                          borderWidth={getBorderWidth(isPreBuiltSelected)}
                          marginRight='10px'
                          onClick={() => handleProgramTypeClick('PRE-BUILT')}
                        >
                          <TextBoxTitleContainer>
                            <TextBoxTitle>Pre-Built Program</TextBoxTitle>
                          </TextBoxTitleContainer>
                          <TextBoxContentContainer>
                            <TextBoxContent>
                              I have built workouts on this calendar already.
                            </TextBoxContent>
                          </TextBoxContentContainer>
                        </TextBoxContainer>
                        <TextBoxContainer
                          borderColor={getBorderColor(isOngoingSelected)}
                          borderWidth={getBorderWidth(isOngoingSelected)}
                          marginLeft='10px'
                          onClick={() => handleProgramTypeClick('ONGOING')}
                        >
                          <TextBoxTitleContainer>
                            <TextBoxTitle>Ongoing Program</TextBoxTitle>
                          </TextBoxTitleContainer>
                          <TextBoxContentContainer>
                            <TextBoxContent>
                              I will be adding workouts to this calendar on an
                              ongoing basis.
                            </TextBoxContent>
                          </TextBoxContentContainer>
                        </TextBoxContainer>
                      </TextBoxOuterWrapper>
                    </FormGroup>
                  )}
                  {isCalendarSelected && isOngoingSelected && (
                    <FormGroup>
                      <Text
                        styledTextProps={{ fontSize: '16px' }}
                        styledWrapperProps={{ marginTop: '15px' }}
                      >
                        Subscriber will be placed in calendar on day of
                        registration.
                      </Text>
                    </FormGroup>
                  )}
                  {isCalendarSelected && isPreBuiltSelected && (
                    <FormGroup>
                      <FirstWorkoutContainer>
                        <TitlePlusErrorWrapper>
                          <SubText className='toggle-text' fontSize={10}>
                            First workout is on:
                            {' '}
                          </SubText>
                          {formProps.errors.calendarOffsetDate
                            && page2Errors && (
                              <FieldErrorForOffsetDate className='error-text'>
                                {formProps.errors.calendarOffsetDate}
                              </FieldErrorForOffsetDate>
                          )}
                        </TitlePlusErrorWrapper>
                        <DatePicker
                          className='form-control'
                          selected={
                            selectedDate && isCalendarSelected
                              ? Date.parse(selectedDate)
                              : null
                          }
                          onChange={handleDateChange}
                        />
                      </FirstWorkoutContainer>
                    </FormGroup>
                  )}
                  {isCalendarSelected && isPreBuiltSelected && (
                    <FormGroup className='d-flex'>
                      <CheckboxWrapper>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className='checkbox-label'
                          htmlFor='monday-start-field'
                        >
                          <Field
                            className='checkbox'
                            checked={formProps.values.mondayStart}
                            id='monday-start-field'
                            name='monday-start'
                            type='checkbox'
                            onChange={handleMondayStartChange}
                          />
                          <Text styledTextProps={{ fontSize: '14px' }}>
                            Subscriber should start workouts on a Monday
                          </Text>
                        </label>
                      </CheckboxWrapper>
                    </FormGroup>
                  )}
                  {isCalendarSelected
                    && isPreBuiltSelected
                    && (formProps.values.mondayStart
                      || (!formProps.touched.mondayStart
                        && program.startOnDayOfWeek !== null)) && (
                        <FormGroup className='d-flex'>
                          <Text styledTextProps={{ fontSize: '14px' }}>
                            *We recommend scheduling 7 days of
                            &ldquo;lead-in&rdquo; workouts prior to the first
                            workout date so the customer can see workouts
                            immediately after purchase
                          </Text>
                        </FormGroup>
                  )}
                </>
              ) : (
                <>
                  {savedWorkouts?.length > 0 && (
                    <FormGroup>
                      <TitlePlusErrorWrapper>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Assign Saved Workout when Purchased
                        </Text>
                      </TitlePlusErrorWrapper>
                      {savedWorkoutError && (
                        <FieldError className='error-text'>
                          {savedWorkoutError}
                        </FieldError>
                      )}
                      <Select
                        getOptionLabel={(option) => option.programName}
                        getOptionValue={(option) => option.id}
                        isClearable
                        isDisabled={isCalendarSelected}
                        isMulti
                        options={savedWorkouts}
                        value={formProps.values.savedWorkouts}
                        onChange={handleSavedWorkoutsChange}
                      />
                    </FormGroup>
                  )}
                  {formProps.values.savedWorkouts?.length > 0 && (
                    <FormGroup className='d-flex'>
                      <CheckboxWrapper>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className='checkbox-label'
                          htmlFor='agreement-field'
                        >
                          <Field
                            className='checkbox'
                            checked={formProps.values.autoStartProgram}
                            id='agreement-field'
                            name='autoStartProgram'
                            type='checkbox'
                            onChange={handleAutoStartChange}
                          />
                          <Text styledTextProps={{ fontSize: '14px' }}>
                            Automatically start associated programs for athletes
                            when program is purchased.
                          </Text>
                        </label>
                      </CheckboxWrapper>
                    </FormGroup>
                  )}
                </>
              )}
              <FormGroup>
                <Text
                  styledTextProps={{ fontWeight: 700 }}
                  styledWrapperProps={formGroupTextProps}
                >
                  Group Assignment
                </Text>
                <Select
                  defaultValue={formProps.values.groups}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  isClearable
                  isMulti
                  isOptionSelected={(group) => formProps.values.group === group}
                  options={userGroups}
                  onChange={handleGroupsChange}
                />
              </FormGroup>
            </Form>
          </InnerFolderFormWrapper>
        </OuterFolderFormWrapper>
      </AnotherWrapper>
    </ModalWrapper>
  );
};

UpdateProgramSlide2.propTypes = {
  clickedCalendar: PropTypes.bool.isRequired,
  clickedOffsetDate: PropTypes.bool.isRequired,
  flattenedCalendars: PropTypes.instanceOf(Array).isRequired,
  formProps: PropTypes.instanceOf(Object).isRequired,
  page2Errors: PropTypes.bool.isRequired,
  program: PropTypes.instanceOf(Object).isRequired,
  programSourceOptions: PropTypes.instanceOf(Array).isRequired,
  programType: PropTypes.string,
  savedWorkoutError: PropTypes.string.isRequired,
  selectedCalendar: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]).isRequired,
  selectedDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  setClickedCalendar: PropTypes.func.isRequired,
  setClickedOffsetDate: PropTypes.func.isRequired,
  setProgramType: PropTypes.func.isRequired,
  setSavedWorkoutError: PropTypes.func.isRequired,
  setSelectedCalendar: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
};

UpdateProgramSlide2.defaultProps = {
  programType: null,
  selectedDate: undefined,
};

export default UpdateProgramSlide2;
